import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import * as Yup from "yup";
import {
  formatToCurrency,
  getCurrencySymbol,
  parseStringToNumber,
} from "../../../../helperFunctions";
import {
  useGetLoanRepaymentPaystackPaymentMutation,
  useMemberFetchLoanDetailsQuery,
  useVerifyLoanRepaymentMutation,
} from "../../../../redux/features/loans/loans.api";
import {
  LoanRepaymentType,
  SingleLoan,
} from "../../../../redux/features/loans/loans.type";
import { useAppSelector } from "../../../../redux/hooks";
import {
  loanRepaymentTypeMap,
  payStackPopup,
} from "../../../../utils/constants";
import { Checkbox } from "../../../inputs/checkbox";
import { TextInput } from "../../../inputs/textInput";
import { PageLoader } from "../../../loaders/pageLoader";
import { PaymentSelectionModal } from "../paymentSelectionModal";
import { SuccessModal } from "../successModal";

export const MakeRepaymentModal = ({
  modal,
  modalOpen,
  handleModalClose,
  handleModalOpen,
  loanDetails,
  onSuccess,
}: {
  modalOpen: boolean;
  handleModalClose: () => void;
  modal: string;
  handleModalOpen: (name: string) => void;
  loanDetails: SingleLoan;
  onSuccess: () => void;
}) => {
  const { profile } = useAppSelector((state) => state.auth);

  const { data: fullLoanDetails, isFetching: fetchingLoanDetails } =
    useMemberFetchLoanDetailsQuery(
      {
        loan_id: loanDetails.id,
        detail: true,
        coop: loanDetails.Cooperative,
      },
      {
        skip: !(modalOpen && modal === "makeRepaymentModal"),
        refetchOnMountOrArgChange: true,
      }
    );

  const [getPaystackPayment, { isLoading: fetchingPaystackLink }] =
    useGetLoanRepaymentPaystackPaymentMutation();

  const [verifyLoanRepayment] = useVerifyLoanRepaymentMutation();

  const reset = () => {
    resetForm();
    handleModalClose();
  };

  const {
    values,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    isValid,
    resetForm,
    handleChange,
  } = useFormik({
    initialValues: {
      amount: "",
      type: "monthly",
    } as {
      amount: string;
      type: LoanRepaymentType;
    },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      type: Yup.string().required("Repayment type is required"),
      amount: Yup.string().when("type", ([type], schema) => {
        return type === "custom"
          ? schema.required("Amount is required")
          : schema.notRequired();
      }),
    }),
    onSubmit: () => {},
  });

  return (
    <>
      <PaymentSelectionModal
        isModalOpen={modalOpen && modal === "makeRepaymentModal"}
        onModalClose={() => {
          reset();
        }}
        title="Loan Repayment"
        canProccedToPaymentMethod={isValid}
        fetchingPayment={fetchingPaystackLink}
        onMethodSelect={async (method, setMethod) => {
          if (method === "paystack") {
            const resp = await getPaystackPayment({
              id: loanDetails.Cooperative,
              coop: loanDetails.Cooperative,
              type: "repayment",
              repaymenttype: values.type,
              loanid: loanDetails.id,
              amount:
                values.type === "custom"
                  ? parseStringToNumber(values.amount) ?? 0
                  : undefined,
              email: profile?.Email ?? "",
            });

            if (resp.data?.success) {
              payStackPopup.resumeTransaction(resp.data.data.data.access_code, {
                onSuccess: async (successData) => {
                  handleModalOpen("loanRepaymentSuccessModal");

                  await verifyLoanRepayment({
                    coop: loanDetails.Cooperative,
                    trxref: successData.trxref,
                    amount: parseStringToNumber(values.amount) ?? 0,
                    reference: successData.reference,
                    mode: "paystack",
                    loanId: loanDetails.id,
                  });

                  onSuccess();
                },
                onError: () => {
                  reset();
                },
                onCancel: () => {
                  reset();
                },
              });
            } else {
              setMethod(undefined);
            }
          }
        }}
      >
        <div className="flex flex-col gap-4">
          {Object.keys(loanRepaymentTypeMap).map((k) => {
            const key = k as LoanRepaymentType;

            return (
              <Checkbox
                key={key}
                checked={key === values.type}
                onCheck={() => {
                  setFieldValue("type", k);
                }}
                onUnCheck={() => {}}
                text={
                  <div className="flex flex-1 items-center justify-between">
                    {loanRepaymentTypeMap[key].text}

                    {fetchingLoanDetails && key !== "custom" ? (
                      <PageLoader
                        className="!min-h-0 !w-fit"
                        width={20}
                        height={20}
                        variant="threedots"
                        spinnerColor="#077a55"
                      />
                    ) : undefined}

                    {!fetchingLoanDetails &&
                    key !== "custom" &&
                    fullLoanDetails?.data ? (
                      <span className="font-bold text-pry-green-200 text-xs">
                        {formatToCurrency(
                          key === "full"
                            ? fullLoanDetails?.data?.Fullamount ?? 0
                            : fullLoanDetails?.data?.Monthly ?? 0,
                          { currency: "NGN" }
                        )}
                      </span>
                    ) : undefined}
                  </div>
                }
                iconDim={24}
                className="font-medium w-full"
              />
            );
          })}

          {values.type === "custom" ? (
            <NumericFormat
              suffix={undefined}
              prefix={getCurrencySymbol("NGN")}
              decimalScale={2}
              thousandSeparator
              allowLeadingZeros={false}
              customInput={TextInput}
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              name={"amount"}
              value={values?.amount ?? ""}
              placeholder="Enter payment amount"
              hasIcon={false}
              errors={errors?.amount && touched?.amount ? errors?.amount : ""}
              inputClass=""
              className=""
              customIconPosition="left"
            />
          ) : undefined}
        </div>
      </PaymentSelectionModal>

      <SuccessModal
        message={"Loan repayment made successfully"}
        subText={undefined}
        isModalOpen={modalOpen && modal === "loanRepaymentSuccessModal"}
        onModalClose={() => {
          reset();
        }}
      />
    </>
  );
};
